import { type RoleKey } from "@/features/roles/model/roles-types"

export type MemberRole = RoleKey

export enum MemberStatus {
  Active = "active",
  Inactive = "inactive",
  Visitor = "visitor",
  Invited = "invited",
  PendingConfirmation = "pending_confirmation",
  Suspended = "suspended",
  Deceased = "deceased",
  Deactivated = "deactivated",
}
export enum MemberCreatedVia {
  Dashboard = "DASHBOARD",
  Link = "LINK",
  Import = "IMPORT",
}
export enum MemberGender {
  Male = "male",
  Female = "female",
}
export enum MemberMaritalStatus {
  Single = "SINGLE",
  Married = "MARRIED",
  Engaged = "ENGAGED",
  Divorced = "DIVORCED",
  Widowed = "WIDOWED",
  Separated = "SEPARATED",
  Unknown = "UNKNOWN",
}
export enum MemberMarriageType {
  None = "NONE",
  ChurchOnly = "CHURCH_ONLY",
  CivilOnly = "CIVIL_ONLY",
  ChurchAndCivil = "CHURCH_AND_CIVIL",
  TraditionalOnly = "TRADITIONAL_ONLY",
  Unknown = "UNKNOWN",
}
export enum EmploymentType {
  FullTime = "full_time",
  Contractor = "contractor",
  Intern = "intern",
}
export enum MemberDocumentCategory {
  Id = "id",
  Contract = "contract",
  Tax = "tax",
  Other = "other",
}
export enum MemberDocumentStatus {
  Uploaded = "uploaded",
  Missing = "missing",
  Expired = "expired",
}
export type Member = {
  id: string
  workspaceId?: string
  name: string
  firstName?: string
  middleName?: string
  lastName?: string
  email: string | null
  phone?: string
  occupation?: string | null
  address?: string
  notes?: string
  /** Extra answers captured by a purpose form for non-module fields (custom questions). Read-only. */
  customFields?: Record<string, unknown> | null
  createdVia?: MemberCreatedVia
  gender?: MemberGender
  dateOfBirth?: string
  occupation?: string | null
  placeOfBirth?: string
  placeOfBirthCountry?: string
  placeOfBirthCity?: string
  maritalStatus?: MemberMaritalStatus | null
  marriageType?: MemberMarriageType | null
  /** Baptism summary: whether, when, where. null means not recorded. */
  baptized?: boolean | null
  baptizedAt?: string | null
  baptismPlace?: string | null
  originChurchName?: string | null
  fatherFirstName?: string | null
  fatherMiddleName?: string | null
  fatherLastName?: string | null
  motherFirstName?: string | null
  motherMiddleName?: string | null
  motherLastName?: string | null
  spouseFirstName?: string | null
  spouseMiddleName?: string | null
  spouseLastName?: string | null
  guardianFirstName?: string | null
  guardianMiddleName?: string | null
  guardianLastName?: string | null
  branchId?: string
  groupIds?: string[]
  ministryIds?: string[]
  groupNames?: string[]
  ministryNames?: string[]
  avatarUrl?: string
  locale?: MemberLocale
  timezone?: string
  role: MemberRole
  team: string
  status: MemberStatus
  employmentType: EmploymentType
  source?: string
  lastActiveAt: string
  joinedAt: string
}

export enum MemberRelationshipSuggestionType {
  SameFamily = "SAME_FAMILY",
  PossibleSibling = "POSSIBLE_SIBLING",
  PossibleParentChild = "POSSIBLE_PARENT_CHILD",
  PossibleSpouse = "POSSIBLE_SPOUSE",
  PossibleHouseholdMember = "POSSIBLE_HOUSEHOLD_MEMBER",
}

/** A person's verdict on a relationship suggestion: the suggestion status it sets (reopened is the undo). */
export enum RelationshipDecision {
  Confirmed = "CONFIRMED",
  Dismissed = "DISMISSED",
  Reopened = "ACTIVE",
}

export enum MemberRelationshipSuggestionStatus {
  Active = "ACTIVE",
  Inactive = "INACTIVE",
  Dismissed = "DISMISSED",
  Confirmed = "CONFIRMED",
}

export type RelationshipWorkspaceSummary = {
  id: string
  name: string
  slug: string
  logoUrl: string | null
  status: string
}

export type RelationshipBranchSummary = {
  id: string
  workspaceId: string
  name: string
  slug: string
  status: "ACTIVE" | "INACTIVE"
  workspace: RelationshipWorkspaceSummary | null
}

export type RelationshipMemberSummary = {
  id: string
  workspaceId: string
  branchId: string
  firstName: string
  middleName: string | null
  lastName: string
  gender: MemberGender | null
  status: MemberStatus
  email: string | null
  phone: string | null
  avatarUrl: string | null
  branch: RelationshipBranchSummary | null
}

export type MemberRelationshipItem = {
  id: string
  workspaceId: string
  branchId: string | null
  memberId: string
  relatedMemberId: string
  suggestionType: MemberRelationshipSuggestionType
  displayLabel: string | null
  reasonText: string | null
  confidenceScore: number
  reasons: unknown
  status: MemberRelationshipSuggestionStatus
  computedAt: string
  createdAt: string
  updatedAt: string
  relatedMember: RelationshipMemberSummary | null
}

export type GetMemberRelationshipsResponse = {
  member: RelationshipMemberSummary
  items: MemberRelationshipItem[]
}

export type MemberProfileUpdateInput = Partial<
  Pick<
    Member,
    "name" | "firstName" | "middleName" | "lastName" | "phone" | "avatarUrl" | "locale" | "timezone"
  >
>

export type MemberAdminUpdateInput = Pick<
  Member,
  "email" | "role" | "team" | "status" | "employmentType"
> &
  Partial<
    Pick<
      Member,
      "name" | "firstName" | "middleName" | "lastName" | "phone" | "avatarUrl" | "locale" | "timezone"
    >
  >

export type MemberOnboardingInput = {
  workspaceId?: string
  firstName: string
  middleName?: string
  lastName?: string
  gender: MemberGender
  branchId: string
  status: MemberStatus
  email?: string
  dateOfBirth?: string
  placeOfBirth?: string
  placeOfBirthCountry?: string
  placeOfBirthCity?: string
  maritalStatus?: MemberMaritalStatus | null
  marriageType?: MemberMarriageType | null
  /** Baptism summary: whether, when, where. null means not recorded. */
  baptized?: boolean | null
  baptizedAt?: string | null
  baptismPlace?: string | null
  originChurchName?: string | null
  fatherFirstName?: string | null
  fatherMiddleName?: string | null
  fatherLastName?: string | null
  motherFirstName?: string | null
  motherMiddleName?: string | null
  motherLastName?: string | null
  spouseFirstName?: string | null
  spouseMiddleName?: string | null
  spouseLastName?: string | null
  guardianFirstName?: string | null
  guardianMiddleName?: string | null
  guardianLastName?: string | null
  phone?: string
  address?: string
  notes?: string
  avatarUrl?: string | null
  groupIds: string[]
  // Legacy ministry affiliations are deprecated and no longer collected by the UI; kept optional so
  // existing callers/read paths still type-check. Not sent on create.
  ministryIds?: string[]
  // When a member is created from an unmatched form submission, this records the originating submission
  // so the backend can persist Member.sourceFormSubmissionId. Optional — only set by that flow.
  sourceFormSubmissionId?: string
}

export enum MemberInviteStatus {
  Pending = "pending",
  Sent = "sent",
  Accepted = "accepted",
  Expired = "expired",
}
export type MemberInviteRecord = {
  id: string
  memberId: string
  email: string
  status: MemberInviteStatus
  sentAt: string
  sentBy: string
  tokenRef: string
  expiresAt: string
}

export type MemberDocument = {
  id: string
  memberId: string
  name: string
  category: MemberDocumentCategory
  status: MemberDocumentStatus
  expiresAt?: string
  updatedAt: string
}

export enum MemberAction {
  View = "view",
  Edit = "edit",
  ResendInvite = "resend_invite",
  Suspend = "suspend",
  Reactivate = "reactivate",
  Deactivate = "deactivate",
  Delete = "delete",
  UpdateRole = "update_role",
  UpdateTeam = "update_team",
  UpdateStatus = "update_status",
  UpdateEmployment = "update_employment",
}
export type MemberActionPayload = {
  role?: MemberRole
  team?: string
  status?: MemberStatus
  employmentType?: EmploymentType
}

export enum MemberDetailMode {
  View = "view",
  Edit = "edit",
}
export enum MemberLocale {
  Fr = "fr",
  En = "en",
  Rw = "rw",
}
export type MemberUpdateInput = Pick<
  Member,
  "name" | "email" | "role" | "team" | "status" | "employmentType"
>

export type MemberValidationErrors = Partial<
  Record<keyof MemberUpdateInput, string>
>

export enum ChurchMemberStatus {
  Active = "active",
  Inactive = "inactive",
}

export type ChurchMember = {
  id: string
  firstName?: string
  lastName?: string
  phone?: string
  email?: string
  status: ChurchMemberStatus
  groupIds?: string[]
}

export const MEMBER_ROLE_LABELS: Record<MemberRole, string> = {
  admin: "Admin",
  finance: "Finance",
  hr: "HR",
  operations: "Operations",
  storekeeper: "Storekeeper",
  viewer: "Viewer",
}

export const MEMBER_STATUS_LABELS: Record<MemberStatus, string> = {
  [MemberStatus.Active]: "Active",
  [MemberStatus.Inactive]: "Inactive",
  [MemberStatus.Visitor]: "Visitor",
  [MemberStatus.Invited]: "Invited",
  [MemberStatus.PendingConfirmation]: "Pending confirmation",
  [MemberStatus.Suspended]: "Suspended",
  [MemberStatus.Deceased]: "Deceased",
  [MemberStatus.Deactivated]: "Deactivated",
}

export const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
  [EmploymentType.FullTime]: "Full-time",
  [EmploymentType.Contractor]: "Contractor",
  [EmploymentType.Intern]: "Intern",
}

export const MEMBER_DOCUMENT_CATEGORY_LABELS: Record<
  MemberDocumentCategory,
  string
> = {
  [MemberDocumentCategory.Id]: "ID",
  [MemberDocumentCategory.Contract]: "Contract",
  [MemberDocumentCategory.Tax]: "Tax",
  [MemberDocumentCategory.Other]: "Other",
}

export const MEMBER_DOCUMENT_STATUS_LABELS: Record<MemberDocumentStatus, string> = {
  [MemberDocumentStatus.Uploaded]: "Uploaded",
  [MemberDocumentStatus.Missing]: "Missing",
  [MemberDocumentStatus.Expired]: "Expired",
}

export function buildMemberFullName(input: {
  firstName?: string
  middleName?: string
  lastName?: string
  fallback?: string
}) {
  const fullName = [input.firstName, input.middleName, input.lastName]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(" ")
  return fullName || input.fallback || ""
}
